'use client'

import { useUser } from '@auth0/nextjs-auth0'
import { useActionState, useEffect, useMemo, useState, useRef } from 'react'

import {
  applyConditionalLogic,
  getAccountFields,
} from '@/app/account/update-my-details/client/fields'

import type { AccountFields } from '@/app/account/update-my-details/client/fields'

import { updateAccountAction } from '@/app/account/update-my-details/actions/update-account-action'

import {
  FormField,
  TextInput,
  EmailInput,
  SelectInput,
  CheckboxInput,
  GroupedCheckboxInput,
  RadioInput,
  TextareaInput,
} from '@/app/account/update-my-details/client/forms'

import {
  AccountFormState,
  initialAccountFormState,
} from '@/app/account/update-my-details/shared/account-form-state'

/**
 * --------------------------------------------------------------------------
 * AccountFormWrapper
 *
 * @param {Object} props
 * @param {AccountFields} [props.fields={}] - The fields to display in the form (based on AccountFields type in fields.tsx)
 * @param {string} [props.submitText='Submit'] - The text for the submit button
 * @param {Function} [props.handleSuccess] - Optional callback to run on successful form submission
 * @param {React.ReactNode} [props.footerLeftChildren=null] - Optional children to render on the left side of the footer
 * --------------------------------------------------------------------------
 **/
export function AccountFormWrapper({
  fields = {},
  submitText = 'Submit',
  handleSuccess,
  footerLeftChildren = null,
}: {
  fields?: AccountFields
  submitText?: string
  handleSuccess?: () => void
  footerLeftChildren?: React.ReactNode
}) {
  const [initialState, setInitialState] = useState<AccountFormState>(null)

  const { user, isLoading } = useUser()

  // Add user meta to the form state on mount
  useMemo(() => {
    if (user && !isLoading) {
      const userMeta = user?.meta || {}

      setInitialState({
        ...initialAccountFormState,
        ...userMeta,
      })
    }
  }, [user, isLoading])

  if (!initialState) {
    return (
      <div className="py-l text-center">
        <h6>Loading...</h6>
      </div>
    )
  }

  return (
    <AccountForm
      initialState={initialState}
      submitText={submitText}
      fields={fields}
      handleSuccess={handleSuccess}
      footerLeftChildren={footerLeftChildren}
    />
  )
}

/**
 * --------------------------------------------------------------------------
 * AccountForm
 *
 * @param {Object} props
 * @param {AccountFormState} props.initialState - The initial state of the form
 * @param {string} [props.submitText='Submit'] - The text for the submit button
 * @param {AccountFields} [props.fields={}] - The fields to display in the form (based on AccountFields type in fields.tsx)
 * @param {React.ReactNode} [props.footerLeftChildren=null] - Optional children to render on the left side of the footer
 * @param {Function} [props.handleSuccess] - Optional callback to run on successful form submission
 * --------------------------------------------------------------------------
 **/
function AccountForm({
  initialState,
  submitText = 'Submit',
  fields = {},
  handleSuccess,
  footerLeftChildren = null,
}: {
  initialState: AccountFormState
  submitText?: string
  fields?: AccountFields
  footerLeftChildren?: React.ReactNode
  handleSuccess?: () => void
}) {
  // Store two copies of the account fields - one we modify with conditional logic and one we keep as the "original/base"
  const [originalFields, setOriginalFields] = useState<AccountFields>({})
  const [displayFields, setDisplayFields] = useState<AccountFields>({})

  // Use the action state hook to manage form submission state
  const [state, formAction, pending] = useActionState(updateAccountAction, initialState)

  // Create a ref to scroll to the top of the form on success
  const formAnchorRef = useRef(null)

  // Store the form element reference
  const formRef = useRef(null)

  // Used to check if the component has been initialized
  const didInit = useRef(false)

  /**
   * --------------------------------------------------------------------------
   * Load account fields on mount
   *
   * @description Fetches account fields, stores original copy, and applies
   * conditional logic to initialise displayFields.
   * --------------------------------------------------------------------------
   **/
  useEffect(() => {
    if (didInit.current) return
    didInit.current = true

    getAccountFields(fields).then((result) => {
      // Store original, unmodified fields
      setOriginalFields(result)

      // Apply conditional logic to the fields based on the initial state
      const withConditionalLogic = applyConditionalLogic(result, initialState)
      setDisplayFields(withConditionalLogic)
    })
  }, [fields, initialState])

  /**
   * --------------------------------------------------------------------------
   * Handle form change
   *
   * @description Updates displayFields based on current form data
   * whenever an input changes.
   * --------------------------------------------------------------------------
   **/
  const handleFormChange = (e) => {
    const form = formRef.current
    if (!form) return

    const formDataObject = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, any>

    // Apply conditional logic to the display fields based on the form data
    const updatedFields = applyConditionalLogic(originalFields, formDataObject)
    setDisplayFields(updatedFields)
  }

  /**
   * --------------------------------------------------------------------------
   * Scroll to the top of the form when the form is submitted successfully
   * --------------------------------------------------------------------------
   **/
  useEffect(() => {
    if (state.success && formAnchorRef.current) {
      formAnchorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      handleSuccess?.()
    }
  }, [state.success, handleSuccess])

  // Show loading state until displayFields are loaded
  if (!Object.keys(displayFields).length) {
    return (
      <div className="py-l text-center">
        <h6>Loading...</h6>
      </div>
    )
  }

  return (
    <div className="w-full @container">
      <div className="anchor" ref={formAnchorRef}></div>

      {state.success ? (
        <div className="py-m">
          <div className="notice notice--success">
            <p>Your account details have been successfully updated.</p>
          </div>
        </div>
      ) : null}

      {state.errors.length ? (
        <div className="py-m">
          <div className="notice notice--error">
            <p>Your submission contains errors. Please review and fix before trying again.</p>
          </div>
        </div>
      ) : null}

      {state.apiError ? (
        <div className="py-m">
          <div className="notice notice--error">{state.apiError}</div>
        </div>
      ) : null}

      <form action={formAction} ref={formRef} onChange={handleFormChange}>
        <div className="grid grid-cols-2 gap-m s:grid-cols-1">
          {Object.keys(displayFields).map((name) => {
            const { placeholder, required, type, choices, charLimit, classNames } =
              displayFields[name]

            const input = (() => {
              switch (type) {
                case 'text':
                  return (
                    <TextInput
                      name={name}
                      placeholder={placeholder}
                      defaultValue={state[name]}
                      charLimit={charLimit}
                      required={required}
                      className="input input--text"
                    />
                  )
                case 'email':
                  return (
                    <EmailInput
                      name={name}
                      placeholder={placeholder}
                      defaultValue={state[name]}
                      charLimit={charLimit}
                      required={required}
                      className="input input--text"
                    />
                  )
                case 'textarea':
                  return (
                    <TextareaInput
                      name={name}
                      placeholder={placeholder}
                      defaultValue={state[name]}
                      charLimit={charLimit}
                      required={required}
                      className="input input--text"
                    />
                  )
                case 'select':
                  return (
                    <SelectInput
                      name={name}
                      defaultValue={state[name]}
                      className="input input--text"
                      required={required}
                      choices={choices}
                    />
                  )
                case 'checkbox':
                  return (
                    <CheckboxInput
                      name={name}
                      defaultValue={state[name]}
                      classNames={classNames}
                      choices={choices}
                    />
                  )
                case 'grouped_checkbox':
                  return (
                    <GroupedCheckboxInput
                      name={name}
                      defaultValue={state[name]}
                      classNames={classNames}
                      choices={choices}
                    />
                  )
                case 'radio':
                  return (
                    <RadioInput
                      name={name}
                      defaultValue={state[name]}
                      className="input input--text"
                      choices={choices}
                    />
                  )
                default:
                  return null
              }
            })()

            return (
              <FormField
                key={name}
                name={name}
                {...displayFields[name]}
                defaultValue={state[name]}
                error={state.errors[name] || null}>
                {input}
              </FormField>
            )
          })}
        </div>

        <div className="form-footer mt-m flex flex-row gap-s border-t pt-m xs:flex-col xs-up:items-center xs-up:justify-between">
          {footerLeftChildren ? <div>{footerLeftChildren}</div> : null}

          <button
            type="submit"
            disabled={pending}
            className="button btn btn--solid ml-auto min-w-[140px] s:grow">
            {pending ? 'Saving...' : submitText}
          </button>
        </div>
      </form>
    </div>
  )
}
